// app/layout.js or wherever you have your main layout
'use client';
import { Provider } from 'react-redux';
import { store } from '@/redux/store';
import { ToastContainer } from 'react-toastify';
import AuthWrapper from '@/components/AuthWrapper/page';
import ChatBot from '@/components/ChatBot/ChatBot';
import { useSEO } from '@/hooks/useSEO';
import 'react-toastify/dist/ReactToastify.css';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import FacebookPixel from '@/components/Analytics/FacebookPixel';
import GoogleTagManager, { GoogleTagManagerNoScript } from '@/components/Analytics/GoogleTagManager';
import MicrosoftClarity from '@/components/Analytics/MicrosoftClarity';
import SecurityWrapper from '@/components/SecurityWrapper';

export default function RootLayout({ children }) {
  const seoComponent = useSEO();
  const pathname = usePathname();
  const [isNotFoundPage, setIsNotFoundPage] = useState(false);
  const [isErrorPageState, setIsErrorPageState] = useState(false);
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isMaintenancePage, setIsMaintenancePage] = useState(false);

  const isAdminRoute = pathname.startsWith('/control') || pathname.startsWith('/(superadmin)');
  const isErrorPage = pathname === '/unauthorized' || pathname === '/forbidden' || pathname === '/service-unavailable' || pathname === '/error' || pathname === '/404';
  const isMaintenance = pathname === '/maintenance';

  useEffect(() => {
    // Check if we're on a 404 or error page by checking the body data attribute
    const checkErrorPages = () => {
      const pageType = document.body.getAttribute('data-page-type');
      setIsNotFoundPage(pageType === 'not-found');
      setIsErrorPageState(pageType === 'error');
      setIsServiceUnavailable(pageType === 'service-unavailable');
      setIsUnauthorized(pageType === 'unauthorized');
      setIsMaintenancePage(pageType === 'maintenance' || pathname === '/maintenance');
    };

    checkErrorPages();

    // Use MutationObserver to watch for changes to the data attribute
    const observer = new MutationObserver(checkErrorPages);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-page-type']
    });

    return () => observer.disconnect();
  }, [pathname]);

  return (
    <html lang="en" className="overflow-x-hidden">
      <head>
        <GoogleTagManager />
        <title>Hyrelancer</title>
        <MicrosoftClarity />
        {seoComponent}
      </head>
      <body className="overflow-x-hidden">
        <GoogleTagManagerNoScript />
        <Provider store={store}>
          <FacebookPixel />
          <AuthWrapper>
            <SecurityWrapper>
              {children}
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                toastStyle={{
                  fontSize: '14px',
                  borderRadius: '8px',
                }}
              />
              {!isAdminRoute && !isErrorPage && !isNotFoundPage && !isErrorPageState && !isServiceUnavailable && !isUnauthorized && !isMaintenancePage && <ChatBot />}
            </SecurityWrapper>
          </AuthWrapper>
        </Provider>
      </body>
    </html>
  );
}